import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, CardBody, Button } from 'reactstrap';
import { MDBDataTable } from 'mdbreact';
import SweetAlert from 'react-bootstrap-sweetalert';

import { activateAuthLayout } from '../../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import CAFFormModal from './caf-form-modal.js';

const DATA = gql`
  query getData {
    CAFTemplates {
      CAFTemplateId
      Handle
      Name
      Description
      Form
      IsDefault
      HasSite
      PropagateAddress
      CAFSectionId
      CAFSection {
        CAFSectionId
        Name
      }
    }
    CAFSections {
      CAFSectionId
      Name
      SortOrder
    }
  }
`;

const CREATE_CAF_TEMPLATE = gql`
  mutation createCAFTemplate($CAFTemplateInput: CAFTemplateInput) {
    createCAFTemplate(input: $CAFTemplateInput) {
      CAFTemplateId
      Handle
      Name
      Description
      Form
      IsDefault
      HasSite
      PropagateAddress
      CAFSectionId
      CAFSection {
        CAFSectionId
        Name
      }
    }
  }
`;

const UPDATE_CAF_TEMPLATE = gql`
  mutation updateCAFTemplate($CAFTemplateInput: CAFTemplateInput) {
    updateCAFTemplate(input: $CAFTemplateInput) {
      CAFTemplateId
      Handle
      Name
      Description
      Form
      IsDefault
      HasSite
      PropagateAddress
      CAFSectionId
      CAFSection {
        CAFSectionId
        Name
      }
    }
  }
`;

const DELETE_CAF_TEMPLATE = gql`
  mutation deleteCAFTemplate($CAFTemplateId: Int!) {
    deleteCAFTemplate(CAFTemplateId: $CAFTemplateId) {
      CAFTemplateId
      Name
    }
  }
`;

const CAFFormBuilder = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  // initialize states
  const [cafTemplatesData, setCAFTemplatesData] = useState(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCAFFormModal, setShowCAFFormModal] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [handle, setHandle] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [hasSite, setHasSite] = useState(false);
  const [propagateAddress, setPropagateAddress] = useState(false);
  const [cafSectionId, setCafSectionId] = useState(null);
  const [cafFormModalMode, setCafFormModalMode] = useState('create');

  // initialize apollo graphql queries and mutations
  const { data, refetch } = useQuery(DATA);
  const [createCAFTemplate] = useMutation(CREATE_CAF_TEMPLATE);
  const [updateCAFTemplate] = useMutation(UPDATE_CAF_TEMPLATE);
  const [deleteCAFTemplate] = useMutation(DELETE_CAF_TEMPLATE);

  const showCreateTemplate = () => {
    setShowCAFFormModal(true);
    setCafFormModalMode('create');
    setSelectedTemplate(null);
    setHandle('');
    setName('');
    setDescription('');
    setIsDefault(false);
    setHasSite(false);
    setPropagateAddress(false);
    setCafSectionId(null);
  };

  const toggleCAFFormModal = useCallback(() => {
    setShowCAFFormModal(prev => !prev);
  }, []);

  const showEditTemplate = useCallback((template) => {
    toggleCAFFormModal();
    setCafFormModalMode('edit');
    setSelectedTemplate(template);
    setHandle(template.Handle);
    setName(template.Name);
    setDescription(template.Description || '');
    setIsDefault(template.IsDefault || false);
    setHasSite(template.HasSite || false);
    setPropagateAddress(template.PropagateAddress || false);
    setCafSectionId(template.CAFSectionId || null);
  }, [toggleCAFFormModal]);

  const duplicateTemplate = useCallback((template) => {
    if (template && template.Name) {
      const input = {
        Handle: template.Handle,
        Name: `${template.Name} (Copy)`,
        Description: template.Description,
        Form: template.Form,
        IsDefault: false,
        CAFSectionId: template.CAFSectionId || null,
      };
      createCAFTemplate({ variables: { CAFTemplateInput: input } }).then(() => refetch());
    }
  }, [createCAFTemplate, refetch]);

  const saveCAFTemplate = (template) => {
    const missingFields = [];
    if (!handle) missingFields.push('Handle');
    if (!name) missingFields.push('Name');
    if (missingFields.length > 0) {
      setValidationError({ message: 'Required fields missing:', items: missingFields });
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(handle)) {
      setValidationError({ message: 'Handle cannot contain spaces or special characters.' });
      return;
    }
    const unnamed = template.filter(f => !f.name);
    if (unnamed.length > 0) {
      setValidationError({ message: 'Name missing for fields:', items: unnamed.map(f => f.title || '(untitled)') });
      return;
    }
    const invalidNames = template.filter(f => !/^[a-zA-Z0-9_-]+$/.test(f.name));
    if (invalidNames.length > 0) {
      setValidationError({ message: 'Field names cannot contain spaces or special characters:', items: invalidNames.map(f => f.name) });
      return;
    }
    const names = template.map(f => f.name);
    const duplicates = names.filter((name, i) => names.indexOf(name) !== i);
    if (duplicates.length > 0) {
      setValidationError({ message: 'Duplicate field names found:', items: [...new Set(duplicates)] });
      return;
    }
    const input = {
      Handle: handle,
      Name: name,
      Description: description || null,
      Form: JSON.stringify(template),
      IsDefault: isDefault,
      HasSite: hasSite,
      PropagateAddress: hasSite ? propagateAddress : false,
      CAFSectionId: cafSectionId || null,
    };
    if (cafFormModalMode === 'create') {
      createCAFTemplate({
        variables: {
          CAFTemplateInput: input,
        },
      }).then(() => {
        setShowCAFFormModal(false);
        refetch();
      });
    } else {
      updateCAFTemplate({
        variables: {
          CAFTemplateInput: {
            ...input,
            CAFTemplateId: parseInt(selectedTemplate.CAFTemplateId, 10),
          },
        },
      }).then(() => {
        setShowCAFFormModal(false);
        refetch();
      });
    }
  };

  const deleteSelectedCAFTemplate = () => {
    deleteCAFTemplate({
      variables: {
        CAFTemplateId: deleteTemplateId,
      },
    }).then(() => refetch());
    setDeleteTemplateId(null);
  };

  // component mounted/willmount
  useEffect(() => {
    if (data && cafTemplatesData === null) {
      const cafTemplates = data.CAFTemplates.map((template, index) => {
        return {
          Name: template.Name,
          CAFSection: template.CAFSection ? template.CAFSection.Name : '—',
          Action: (
            <div>
              <Button
                color="primary"
                size="sm"
                className="mr-2"
                onClick={() => duplicateTemplate(template)}
              >
                Duplicate
              </Button>
              <Button
                color="info"
                size="sm"
                className="mr-2"
                onClick={() => showEditTemplate(template)}
              >
                Edit
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={() => setDeleteTemplateId(template.CAFTemplateId)}
              >
                Delete
              </Button>
            </div>
          ),
        };
      });
      setCAFTemplatesData({
        columns: [
          {
            label: [
              'Template Name',
              <i key={`bundle-name`} className="fa fa-sort ml-2"></i>,
            ],
            field: 'Name',
            sort: 'asc',
          },
          {
            label: [
              'Section',
              <i key={`bundle-section`} className="fa fa-sort ml-2"></i>,
            ],
            field: 'CAFSection',
            sort: 'asc',
          },
          {
            label: 'Action',
            field: 'Action',
            sort: 'disabled',
          },
        ],
        rows: cafTemplates,
      });
    }

    return () => {
      if (data && cafTemplatesData !== null) {
        setCAFTemplatesData(null);
      }
    };
  }, [data, cafTemplatesData, duplicateTemplate, showEditTemplate]);

  return (
    <React.Fragment>
      <CAFFormModal
        isOpen={showCAFFormModal}
        toggleModal={toggleCAFFormModal}
        handle={handle}
        setHandle={setHandle}
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        isDefault={isDefault}
        setIsDefault={setIsDefault}
        hasSite={hasSite}
        setHasSite={setHasSite}
        propagateAddress={propagateAddress}
        setPropagateAddress={setPropagateAddress}
        cafSectionId={cafSectionId}
        setCafSectionId={setCafSectionId}
        cafSections={data ? data.CAFSections : []}
        mode={cafFormModalMode}
        save={saveCAFTemplate}
        template={selectedTemplate}
      />
      {validationError && (
        <SweetAlert
          error
          title="Invalid Template"
          onConfirm={() => setValidationError(null)}
          confirmBtnText="OK"
        >
          <div>
            <p>{validationError.message}</p>
            <ul style={{ textAlign: 'left' }}>
              {validationError.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        </SweetAlert>
      )}
      {deleteTemplateId && (
        <SweetAlert
          warning
          title="Confirm"
          onConfirm={deleteSelectedCAFTemplate}
          onCancel={() => setDeleteTemplateId(null)}
          showCancel
          showConfirm
          confirmBtnText="Delete"
          cancelBtnText="Cancel"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="secondary"
        >
          Are you sure you want to delete this template?
        </SweetAlert>
      )}
      <div className="content">
        <div className="container-fluid">
          <div className="page-title-box">
            <Row className="align-items-center">
              <Col sm="6">
                <h4 className="page-title">CAF Templates</h4>
              </Col>
              <Col sm="6" className="text-right">
                <Button color="success" onClick={() => showCreateTemplate()}>
                  Create New CAF Template
                </Button>
              </Col>
            </Row>
          </div>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {data && cafTemplatesData && (
                    <MDBDataTable
                      sortable
                      striped
                      borderless
                      theadColor="table-dark"
                      hover
                      responsive
                      noBottomColumns
                      noRecordsFoundLabel={'No CAF Template found'}
                      order={['Name', 'asc']}
                      data={cafTemplatesData}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(CAFFormBuilder);
